import { useState, useEffect, useCallback } from 'react';
import { potreroService, actividadService } from '../services/supabase';

export function usePotrero(usuarioId, campoId) {
  const [potreros, setPotreros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar potreros desde BD
  const loadPotreros = useCallback(async () => {
    if (!usuarioId || !campoId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await potreroService.getAll(usuarioId, campoId);
      setPotreros(data || []);
    } catch (e) {
      setError(e.message);
      console.error('Error cargando potreros:', e);
    } finally {
      setLoading(false);
    }
  }, [usuarioId, campoId]);

  // Auto-cargar al inicializar y cada vez que cambia el campo.
  // Se vacía ANTES de pedir: si no, mientras llega la respuesta quedan en pantalla
  // los potreros del campo anterior, y otros efectos los reescriben como si fueran
  // del campo nuevo (los datos de un campo se filtraban al otro).
  // El flag `cancel` descarta respuestas que llegan tarde tras otro cambio de campo.
  useEffect(() => {
    let cancel = false;
    setPotreros([]);
    if (!usuarioId || !campoId) return undefined;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await potreroService.getAll(usuarioId, campoId);
        if (!cancel) setPotreros(data || []);
      } catch (e) {
        if (!cancel) { setError(e.message); console.error('Error cargando potreros:', e); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [usuarioId, campoId]);

  // Crear potrero
  const createPotrero = useCallback(async (potrero) => {
    if (!usuarioId || !campoId) throw new Error("usuarioId y campoId requeridos");
    try {
      const saved = await potreroService.create(usuarioId, campoId, potrero);
      setPotreros(prev => [...prev, saved]);
      return saved;
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, [usuarioId, campoId]);

  // Actualizar potrero
  const updatePotrero = useCallback(async (potreroId, updates) => {
    try {
      const updated = await potreroService.update(potreroId, updates);
      setPotreros(prev => prev.map(p => p.id === potreroId ? updated : p));
      return updated;
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  // Eliminar potrero (soft delete)
  const deletePotrero = useCallback(async (potreroId) => {
    try {
      await potreroService.delete(potreroId);
      setPotreros(prev => prev.filter(p => p.id !== potreroId));
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  return {
    potreros,
    setPotreros,
    loading,
    error,
    loadPotreros,
    createPotrero,
    updatePotrero,
    deletePotrero,
  };
}
